import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Portal, Typography } from "@mui/material";
import { ds } from "../../styles/designSystem";

/**
 * 본문 @ 멘션 자동완성 — Portal Popper 기반 공용 컴포넌트.
 *
 * <h3>왜 컴포넌트화했는가</h3>
 * 이전엔 {@link RichTextEditor} 내부에 300줄짜리 인라인 로직(상태 6개, useEffect 3개, Portal+Paper 렌더)
 * 으로 매몰되어 있었다. 게다가 채널 <b>댓글</b>은 리치에디터가 아니라 평범한 textarea 라서
 * {@code FeedTab.jsx} 안에 popper·flip·후보필터를 통째로 한 벌 더 갖고 있었다 — 같은 UI 가 두 구현으로
 * 갈라져 한쪽(댓글)만 키보드 네비게이션이 없었다. 본 컴포넌트가 그 둘을 모두 흡수한다.
 *
 * <h3>두 가지 입력 모드</h3>
 * 캐럿을 읽고 텍스트를 치환하는 방법만 다르고, 후보 필터·랭킹·popper 렌더·키보드 네비는 공유한다.
 * <ul>
 *   <li><b>editor 모드</b> — {@code editor} 를 주면 Tiptap. 캐럿 좌표를 {@code coordsAtPos} 로 정확히 잡아
 *       popper 를 커서 바로 아래 띄운다. (채널 게시글 본문)</li>
 *   <li><b>input 모드</b> — {@code inputRef/value/onChange} 를 주면 평범한 textarea·input.
 *       textarea 는 캐럿 좌표를 직접 잴 수 없어(미러 div 를 따로 그려야 한다) 입력 요소 자체의
 *       사각형에 붙인다 — 기존 댓글 popper 와 같은 동작이다. (채널 댓글)</li>
 * </ul>
 *
 * <h3>props</h3>
 * <ul>
 *   <li>{@code editor} — Tiptap editor 인스턴스 (editor 모드)</li>
 *   <li>{@code inputRef} — textarea/input DOM ref, {@code value}/{@code onChange} 와 함께 (input 모드)</li>
 *   <li>{@code members} — 정적 후보 배열 ({id, name, deptName, positionName, employeeCode, retired} or {employee: {...}}).
 *       <b>OR</b> {@code fetcher(query) => Promise&lt;array&gt;} — 비동기 모드.</li>
 *   <li>{@code maxItems=8} — 표시 후보 최대 개수</li>
 *   <li>{@code onPicked(emp, name)} — 선택 시 콜백. 외부에서 멘션 발송 등 부수효과 트리거용.</li>
 *   <li>{@code debounceMs=300} — fetcher 모드 디바운스</li>
 * </ul>
 *
 * <h3>활성/비활성</h3>
 * members 도 fetcher 도 없으면 마운트 자체에서 아무 동작 안 함 — 기존 RichTextEditor 의
 * "mentionMembers 비어있으면 비활성" 정책 유지.
 */

/** 커서 직전 "@쿼리" 토큰. 공백·@ 를 만나면 끊긴다(= 스페이스 치면 자동으로 닫힘). */
const MENTION_TOKEN_RE = /@([^\s@]*)$/;

const MAX_H = 280;

/**
 * 커서 직전의 멘션 쿼리. 멘션 토큰이 없으면 null(= popper 닫힘).
 * 빈 문자열("@" 만 친 상태)과 null 은 뜻이 다르므로 falsy 로 뭉뚱그리면 안 된다.
 */
function readMentionQuery(text, cursor) {
    const m = (text ?? "").slice(0, cursor).match(MENTION_TOKEN_RE);
    return m ? m[1] : null;
}

/**
 * 커서 직전 "@쿼리" 토큰을 "@이름 " 으로 치환한 결과 — {value, caret}. 토큰이 없으면 null.
 *
 * <p>순수 함수로 뽑아둔 이유: 문장 <b>중간</b>에 멘션을 넣을 때 캐럿을 되돌릴 위치 계산이
 * off-by-one 나기 딱 좋다. 예전 댓글 구현은 캐럿 복원을 아예 안 해서 문장 중간에 멘션을 고르면
 * 커서가 엉뚱한 곳에 남았다.
 */
function replaceMentionToken(value, cursor, name) {
    const cur = value ?? "";
    const m = cur.slice(0, cursor).match(MENTION_TOKEN_RE);
    if (!m) return null;
    const head = cur.slice(0, cursor - m[0].length);
    const tail = cur.slice(cursor);
    const inserted = `@${name} `;
    return { value: head + inserted + tail, caret: head.length + inserted.length };
}

/**
 * 후보 매칭 점수 — 낮을수록 위. 매칭 안 되면 -1.
 *
 * <p>이름 substring 단독 매칭이던 것을 부서·직위·사번까지 넓혔다. "@김" 처럼 이름을 아는 경우만
 * 상정했지만 실제로는 "그 개발팀 사람" 처럼 소속으로 더듬는 경우가 많고, 채널 멤버가 수십 명이면
 * 이름 첫 글자만으로는 후보가 안 좁혀진다.
 *
 * <p>랭킹을 두는 이유는 백엔드 {@code searchActiveForSuggest} 와 같다 — 단순 substring 만 쓰면
 * "@test2" 검색에 "test20" 이 먼저 잡혀 정작 정확히 일치하는 후보가 maxItems 컷에서 잘린다.
 * 이름 매칭을 부서·직위 매칭보다 항상 위에 두는 것도 같은 이유다(이름을 친 사람의 의도가 우선).
 *
 * <p>이메일은 일부러 뺐다 — 멘션 팝업에서 이메일을 타이핑하는 사용자는 없고,
 * 그걸 넣자고 {@code EmployeeSimpleDto}(투표자·담당자·일정 참여자에도 쓰인다) 에
 * 이메일을 실어 보낼 이유가 없다.
 */
function scoreMentionCandidate(emp, q) {
    if (!emp) return -1;
    // 퇴사자는 후보에서 제외 — 채널 멤버로는 남아있어도(관리자가 내보내야 보인다) 멘션 대상은 아니다.
    // 알림이 가도 받을 사람이 없고, 후보에 뜨면 "아직 다니나?" 로 잘못 읽힌다.
    if (emp.retired === true) return -1;

    const name = (emp.name || "").toLowerCase();
    if (!q) return name ? 3 : 4;   // 빈 쿼리(@ 만 친 상태) — 전원 노출, 이름순은 호출 측 정렬 유지

    if (name === q) return 0;
    if (name.startsWith(q)) return 1;
    if (name.includes(q)) return 2;

    // 이름이 안 맞을 때만 소속·사번을 본다.
    const rest = [
        emp.deptName || emp.department,
        emp.positionName || emp.position,
        emp.employeeCode,
    ];
    for (const f of rest) {
        const v = (f || "").toString().toLowerCase();
        if (!v) continue;
        if (v.startsWith(q)) return 5;
        if (v.includes(q)) return 6;
    }
    return -1;
}

/** 후보 배열 → 매칭·랭킹·상위 N 컷. 동점이면 원래 순서 유지(안정 정렬). */
function filterMentionCandidates(candidates, query, maxItems) {
    const q = (query || "").toLowerCase();
    return (candidates || [])
        .map((m, i) => ({ m, i, score: scoreMentionCandidate(m.employee || m, q) }))
        .filter(x => x.score >= 0)
        .sort((a, b) => (a.score - b.score) || (a.i - b.i))
        .slice(0, maxItems)
        .map(x => x.m);
}

export default function MentionAutocomplete({
    editor,
    inputRef,
    value,
    onChange,
    members,
    fetcher,
    maxItems = 8,
    onPicked,
    debounceMs = 300,
}) {
    const enabled = (Array.isArray(members) && members.length > 0) || typeof fetcher === "function";
    const mode = editor ? "editor" : (inputRef ? "input" : null);

    // popper 좌표 + 현재 쿼리. cursorTop/Bottom 은 viewport 좌표로 보관 (flip 판단용).
    // left/width 는 캐럿이 아니라 <b>입력 요소</b> 기준이다 — 시스템의 다른 드롭다운(Select·ComboBox)이
    // 전부 트리거 폭에 딱 맞춰 열리므로, 멘션만 캐럿 옆에 좁게 뜨면 혼자 다른 컴포넌트로 보인다.
    const [mentionState, setMentionState] = useState({
        open: false, query: "", cursorTop: 0, cursorBottom: 0, left: 0, width: 0,
    });
    const [activeIdx, setActiveIdx] = useState(0);

    // 후보 — 정적 모드는 즉시 필터, 비동기 모드는 debounce 후 fetcher 호출.
    const [asyncOpts, setAsyncOpts] = useState([]);
    const debounceRef = useRef(null);

    // insert 직후 되돌려놓을 캐럿 위치 (input 모드) — onChange 로 리렌더된 뒤 복원해야 한다.
    const pendingCaretRef = useRef(null);

    const filtered = useMemo(() => {
        if (!enabled) return [];
        const pool = typeof fetcher === "function" ? asyncOpts : (members || []);
        // 비동기 모드는 서버가 이미 랭킹해서 주므로 클라이언트 재정렬 없이 퇴사자만 거른다.
        if (typeof fetcher === "function") {
            return pool.filter(m => scoreMentionCandidate(m.employee || m, "") >= 0).slice(0, maxItems);
        }
        return filterMentionCandidates(pool, mentionState.query, maxItems);
    }, [enabled, members, fetcher, asyncOpts, mentionState.query, maxItems]);

    const close = useCallback(() => {
        setMentionState(s => (s.open ? { ...s, open: false } : s));
    }, []);

    // 비동기 fetcher — query 변경 시 debounce 후 호출
    useEffect(() => {
        if (!enabled || typeof fetcher !== "function" || !mentionState.open) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await fetcher(mentionState.query);
                setAsyncOpts(Array.isArray(res) ? res : []);
            } catch {
                setAsyncOpts([]);
            }
        }, debounceMs);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [enabled, fetcher, mentionState.open, mentionState.query, debounceMs]);

    // ─── editor 모드: 커서 직전 텍스트가 @쿼리$ 패턴이면 popper open ───────────
    useEffect(() => {
        if (mode !== "editor" || !editor || !enabled) return;
        const update = () => {
            const { state, view } = editor;
            const { from, empty } = state.selection;
            if (!empty) { close(); return; }
            const $pos = state.doc.resolve(from);
            if (!$pos.parent?.isTextblock) { close(); return; }
            const textBefore = $pos.parent.textBetween(0, $pos.parentOffset, undefined, "​");
            const m = textBefore.match(MENTION_TOKEN_RE);
            if (!m) { close(); return; }
            // 세로는 캐럿 기준(긴 본문에서 쓰던 자리 근처에 떠야 한다), 가로는 편집영역 기준(트리거 폭).
            const coords = view.coordsAtPos(from);
            const box = view.dom.getBoundingClientRect();
            setMentionState({
                open: true,
                query: m[1],
                cursorTop: coords.top,
                cursorBottom: coords.bottom,
                left: box.left,
                width: box.width,
            });
            setActiveIdx(0);
        };
        editor.on("selectionUpdate", update);
        editor.on("update", update);
        return () => {
            editor.off("selectionUpdate", update);
            editor.off("update", update);
        };
    }, [mode, editor, enabled, close]);

    // ─── input 모드: textarea 의 캐럿을 DOM 이벤트로 추적 ────────────────────
    // Tiptap 처럼 구독할 이벤트 스트림이 없으므로 입력/이동을 일으키는 이벤트를 직접 듣는다.
    // (input=타이핑, keyup=화살표·백스페이스 이동, click=마우스로 캐럿 이동)
    useEffect(() => {
        if (mode !== "input" || !enabled) return;
        const el = inputRef?.current;
        if (!el) return;
        const update = () => {
            const q = readMentionQuery(el.value, el.selectionStart ?? 0);
            if (q === null) { close(); return; }
            // textarea 는 캐럿 좌표를 못 재므로 입력 요소 사각형에 붙인다 — 폭도 그대로 물려받는다.
            const rect = el.getBoundingClientRect();
            setMentionState({
                open: true,
                query: q,
                cursorTop: rect.top,
                cursorBottom: rect.bottom,
                left: rect.left,
                width: rect.width,
            });
            setActiveIdx(0);
        };
        el.addEventListener("input", update);
        el.addEventListener("keyup", update);
        el.addEventListener("click", update);
        el.addEventListener("blur", close);
        return () => {
            el.removeEventListener("input", update);
            el.removeEventListener("keyup", update);
            el.removeEventListener("click", update);
            el.removeEventListener("blur", close);
        };
    }, [mode, enabled, inputRef, close]);

    // input 모드 — onChange 반영 후 캐럿을 삽입한 멘션 뒤로 되돌린다.
    // (안 하면 문장 중간에 멘션을 넣었을 때 캐럿이 엉뚱한 곳에 남는다)
    useEffect(() => {
        if (mode !== "input") return;
        const pos = pendingCaretRef.current;
        if (pos == null) return;
        pendingCaretRef.current = null;
        const el = inputRef?.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(pos, pos);
    }, [mode, value, inputRef]);

    const insertMention = useCallback((emp, name) => {
        if (!name) return;

        if (mode === "editor") {
            if (!editor) return;
            const { state } = editor;
            const { from } = state.selection;
            const $pos = state.doc.resolve(from);
            const textBefore = $pos.parent.textBetween(0, $pos.parentOffset, undefined, "​");
            const m = textBefore.match(MENTION_TOKEN_RE);
            if (!m) return;
            const start = from - m[0].length;
            editor.chain().focus()
                .deleteRange({ from: start, to: from })
                .insertContent(`@${name} `)
                .run();
        } else if (mode === "input") {
            const el = inputRef?.current;
            const cur = value ?? "";
            const next = replaceMentionToken(cur, el?.selectionStart ?? cur.length, name);
            if (!next) return;
            pendingCaretRef.current = next.caret;
            onChange?.(next.value);
        } else {
            return;
        }

        setMentionState(s => ({ ...s, open: false }));
        onPicked?.(emp, name);
    }, [mode, editor, inputRef, value, onChange, onPicked]);

    // 키보드 네비 — popper 열린 동안에만 capture.
    // stopPropagation 까지 하는 이유: 이 키들은 popper 가 완전히 소비해야 한다. preventDefault 만
    // 하면 이벤트가 계속 흘러가 입력 요소의 onKeyDown 이 또 처리한다 — 댓글 입력창은 Enter 가
    // "댓글 등록" 이라, 멘션을 고르려고 누른 Enter 가 그대로 댓글까지 올려버린다.
    useEffect(() => {
        if (!mentionState.open || filtered.length === 0) return;
        const consume = (e) => { e.preventDefault(); e.stopPropagation(); };
        const onKey = (e) => {
            if (e.key === "ArrowDown") {
                consume(e);
                setActiveIdx(i => (i + 1) % filtered.length);
            } else if (e.key === "ArrowUp") {
                consume(e);
                setActiveIdx(i => (i - 1 + filtered.length) % filtered.length);
            } else if (e.key === "Enter" || e.key === "Tab") {
                const picked = filtered[activeIdx];
                const emp = picked?.employee || picked;
                if (emp?.name) { consume(e); insertMention(emp, emp.name); }
            } else if (e.key === "Escape") {
                consume(e);
                close();
            }
        };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, [mentionState.open, filtered, activeIdx, insertMention, close]);

    if (!enabled || !mode || !mentionState.open || filtered.length === 0) return null;

    const spaceBelow = window.innerHeight - mentionState.cursorBottom;
    const spaceAbove = mentionState.cursorTop;
    const openAbove = spaceBelow < MAX_H && spaceAbove > spaceBelow;

    /* 종이·항목 스타일은 ds.dropdown — Select 메뉴/ComboBox 팝업과 같은 원본을 쓴다.
       (이 팝퍼는 캐럿 위치에 직접 띄우느라 MUI Autocomplete 가 아니라서 테마 styleOverrides 가
        닿지 않는다. 그래서 스펙을 값으로 받아 같은 모양을 맞춘다 — styles/dropdown.js 참고.) */
    return (
        <Portal>
            <Paper elevation={0} sx={{
                position: "fixed",
                left: mentionState.left,
                width: mentionState.width || undefined,
                ...(openAbove
                    ? { bottom: window.innerHeight - mentionState.cursorTop + 4 }
                    : { top: mentionState.cursorBottom + 4 }),
                zIndex: (theme) => theme.zIndex.modal + 100,
                boxSizing: "border-box",
                maxHeight: MAX_H,
                overflowY: "auto",
                ...ds.dropdown.paper,
                marginTop: 0,       // 트리거와의 간격은 위 top/bottom 계산이 이미 4px 잡아 뒀다
                paddingTop: ds.dropdown.listbox.padding,
                paddingBottom: ds.dropdown.listbox.padding,
            }}>
                {filtered.map((m, i) => {
                    const emp = m.employee || m;
                    const active = i === activeIdx;
                    // 부서·직책 키 호환 (mentionMembers 형식 vs /api/employees/suggest 형식)
                    const dept = emp.deptName || emp.department || "";
                    const pos  = emp.positionName || emp.position || "";
                    return (
                        <Box
                            key={emp.id ?? emp.employeeCode ?? `${emp.name}-${i}`}
                            onMouseDown={(e) => { e.preventDefault(); insertMention(emp, emp.name); }}
                            onMouseEnter={() => setActiveIdx(i)}
                            sx={{
                                ...ds.dropdown.item,
                                paddingLeft: "10px",
                                paddingRight: "10px",
                                cursor: "pointer",
                                // 2줄(이름 / 부서·직위) — 공통 AppAutocomplete 의 사람 항목과 같은 구성.
                                display: "flex", flexDirection: "column", alignItems: "flex-start",
                                ...(active ? { backgroundColor: ds.colors.primaryBg } : null),
                            }}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35, color: ds.colors.textPrimary }}>
                                {emp.name}
                            </Typography>
                            {(dept || pos) && (
                                <Typography sx={{ fontSize: 11, lineHeight: 1.35, color: ds.colors.secondary }}>
                                    {[dept, pos].filter(Boolean).join(" · ")}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Paper>
        </Portal>
    );
}
